import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const defaultUrlPattern = () => "https://[^/]*\\.cyberhaven\\.io/.*";

const tenantStep = {
  title: "Enter your tenant URL",
  items: [
    {
      kind: "hostname" as const,
      label: "Tenant URL",
      placeholder: "mydomain.cyberhaven.io",
      required: true,
      target: { kind: "configuration" as const, field: "tenantUrl" },
      helperText: "Your Cyberhaven instance URL, e.g. mydomain.cyberhaven.io",
    },
  ],
};

const apiKeyConnectionTest = {
  protocol: "http" as const,
  request: ({ tenantUrl }: Record<"tenantUrl", string>) => ({
    url: `https://${tenantUrl}/api/rest/v1/endpoints/list`,
    method: "POST" as const,
    headers: { "content-type": "application/json" },
    body: "{}",
    expectedStatus: [200, 403],
  }),
};

const refreshTokenConnectionTest = {
  protocol: "http" as const,
  request: ({ tenantUrl }: Record<"tenantUrl", string>) => ({
    url: `https://${tenantUrl}/public/v2/incidents/list`,
    method: "POST" as const,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      page_request: { size: 1, sort_by: "trigger_time" },
      include_ai_summaries: false,
    }),
    expectedStatus: [200, 403],
  }),
};

const publicEnvVars = ({ tenantUrl }: Record<"tenantUrl", string>) => ({
  CYBERHAVEN_URL: tenantUrl ? `https://${tenantUrl}` : "",
});

export const cyberhaven = defineProduct({
  name: "Cyberhaven",
  tagLine: "Data detection and response",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "cyberhaven",
      authLabel: "API key",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: `https://${app.config.tenantUrl}/api/rest/v1/token/access`,
            jsonBody: {
              secret: app.secret.secretKey,
            },
          },
          extract: { from: "jsonPath", paths: ["access_token"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        {
          title: "Create an API key",
          items: [
            {
              kind: "markdown",
              content:
                "In the Cyberhaven platform, navigate to **Preferences → Users and API keys**, click the **API Keys** tab, then click **New API Key**. Fill in the details and click **Save**. Copy the key from the confirmation window.",
            },
          ],
        },
        {
          title: "Paste your API key",
          items: [
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "secretKey" },
            },
          ],
        },
        tenantStep,
      ],
      connectionTest: apiKeyConnectionTest,
      publicEnvVars,
      usageHint: () =>
        "Use Cyberhaven REST API v1 endpoints under /api/rest/v1/.",
    }),
    productVariant({
      identifier: "cyberhavenRefreshToken",
      authLabel: "Refresh token",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: `https://${app.config.tenantUrl}/public/v2/auth/token/access`,
            jsonBody: {
              refresh_token: app.secret.refreshToken,
            },
          },
          extract: { from: "jsonPath", paths: ["access_token"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        {
          title: "Paste your refresh token",
          items: [
            {
              kind: "markdown",
              content:
                "Use the long-lived refresh token provided for your Cyberhaven public API integration.",
            },
            {
              kind: "input",
              label: "Refresh token",
              required: true,
              target: { kind: "secrets", field: "refreshToken" },
            },
          ],
        },
        tenantStep,
      ],
      connectionTest: refreshTokenConnectionTest,
      publicEnvVars,
      usageHint: () =>
        "Use Cyberhaven public API v2 endpoints under /public/v2/.",
    }),
  ],
});
